/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { StyleProp, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

const AnimatedPath = Animated.createAnimatedComponent(Path);

export type DigitalSignCanvasRef = {
  clear: () => void;
};

type DrawingCanvasProps = {
  stroke?: string;
  strokeWidth?: number;
  placeholderTextStyle?: StyleProp<TextStyle>;
  style?: StyleProp<ViewStyle>;
  onChange?: (path: string) => void;
};

export const DigitalSignCanvas = memo(
  forwardRef<DigitalSignCanvasRef, DrawingCanvasProps>(
    (
      {
        stroke = '#000',
        strokeWidth = 3,
        placeholderTextStyle,
        style,
        onChange,
      },
      ref,
    ) => {
      const { rpx } = useResponsiveContext();
      const [paths, setPaths] = useState<string[]>([]);
      const currentPath = useSharedValue('');
      const currentPathProps = useAnimatedProps(() => ({
        d: currentPath.value,
      }));

      const gesture = Gesture.Pan()
        .onBegin(e => {
          currentPath.value = `M${e.x},${e.y}`;
        })
        .onUpdate(e => {
          currentPath.value += ` L${e.x},${e.y}`;
        })
        .onEnd(() => {
          const path = `${currentPath.value}`;
          runOnJS(setPaths)([...paths, path]);
          if (onChange) {
            runOnJS(onChange)(path);
          }
        });

      const placeholderStyle = useAnimatedStyle(() => {
        return { opacity: currentPath.value ? 0 : 1 };
      });

      useImperativeHandle(ref, () => {
        return {
          clear: () => {
            setPaths([]);
            currentPath.value = '';
            onChange?.('');
          },
        };
      });

      return (
        <>
          <Svg style={[StyleSheet.absoluteFill, style]}>
            {paths.map((d, i) => (
              <Path
                key={i}
                d={d}
                stroke={stroke}
                strokeWidth={strokeWidth}
                fill="none"
              />
            ))}
            <AnimatedPath
              stroke={stroke}
              strokeWidth={3}
              fill="none"
              animatedProps={currentPathProps}
            />
          </Svg>
          <Animated.View
            style={[StyleSheet.absoluteFill, placeholderStyle]}
            pointerEvents="none"
          >
            <Typography.Label
              color="#ddd"
              size={rpx(32)}
              light
              style={placeholderTextStyle}
            />
          </Animated.View>
          {/* @ts-ignore */}
          <GestureDetector gesture={gesture}>
            <Animated.View
              css={`
                flex: 1;
              `}
            />
          </GestureDetector>
        </>
      );
    },
  ),
);
